export type SkySocietyTierId = "egg" | "sparrow" | "pigeon" | "raven" | "hawk" | "falcon" | "eagle" | "phoenix";
export type SkySocietyTierData = {
  id: SkySocietyTierId;
  name: string;
  tierNumber: number;
  iconPath: string;
  requirements: {
    badges: {
      count: number;
      description: string;
    };
    discord: {
      required: boolean;
      description: string;
    };
    metrics: {
      description: string;
      conditions: string[];
      logicOperator: "AND" | "OR";
    };
  };
  rewards: {
    plumePoints: number;
    other?: string[];
  };
};

export const skySocietyTiers: SkySocietyTierData[] = [
  {
    id: "egg",
    name: "Egg",
    tierNumber: 1,
    iconPath: "/assets/skysociety/egg.avif",
    requirements: {
      badges: {
        count: 0,
        description: "None",
      },
      discord: {
        required: false,
        description: "None",
      },
      metrics: {
        description: "No metrics required",
        conditions: ["None"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 0,
    },
  },
  {
    id: "sparrow",
    name: "Sparrow",
    tierNumber: 2,
    iconPath: "/assets/skysociety/sparrow.avif",
    requirements: {
      badges: {
        count: 5,
        description: "5 Badges",
      },
      discord: {
        required: true,
        description: "Connect Discord and Twitter",
      },
      metrics: {
        description: "No metrics required",
        conditions: ["None"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 50,
    },
  },
  {
    id: "pigeon",
    name: "Pigeon",
    tierNumber: 3,
    iconPath: "/assets/skysociety/pigeon.avif",
    requirements: {
      badges: {
        count: 10,
        description: "10 Badges",
      },
      discord: {
        required: true,
        description: "@LilGoon role",
      },
      metrics: {
        description: "Hub Metrics Required",
        conditions: ["3 trades", "2 day daily spin streak", "Staked Plume for at least 1 day"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 100,
    },
  },
  {
    id: "raven",
    name: "Raven",
    tierNumber: 4,
    iconPath: "/assets/skysociety/raven.avif",
    requirements: {
      badges: {
        count: 20,
        description: "20 Badges",
      },
      discord: {
        required: false,
        description: "None",
      },
      metrics: {
        description: "Hub Metrics Required",
        conditions: ["1 referral", "20 day daily spin streak", "15 trades", "Staked >10 Plume for over 5 days"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 200,
    },
  },
  {
    id: "hawk",
    name: "Hawk",
    tierNumber: 5,
    iconPath: "/assets/skysociety/hawk.avif",
    requirements: {
      badges: {
        count: 30,
        description: "30 Badges",
      },
      discord: {
        required: true,
        description: "@Goon role",
      },
      metrics: {
        description: "Hub Metrics Required",
        conditions: ["2 referrals", "30 day daily spin streak", "30 trades"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 300,
    },
  },
  {
    id: "falcon",
    name: "Falcon",
    tierNumber: 6,
    iconPath: "/assets/skysociety/falcon.avif",
    requirements: {
      badges: {
        count: 40,
        description: "40 Badges",
      },
      discord: {
        required: false,
        description: "None",
      },
      metrics: {
        description: "Hub Metrics Required",
        conditions: ["3 referrals", "Staked >100 Plume for over 10 days"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 500,
    },
  },
  {
    id: "eagle",
    name: "Eagle",
    tierNumber: 7,
    iconPath: "/assets/skysociety/eagle.avif",
    requirements: {
      badges: {
        count: 45,
        description: "45 Badges",
      },
      discord: {
        required: true,
        description: "@ElderGoon role",
      },
      metrics: {
        description: "Hub Metrics Required",
        conditions: ["5 referrals", "50 day daily spin streak", "50 trades", "Staked >100 Plume for over 20 days"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 1000,
    },
  },
  {
    id: "phoenix",
    name: "Phoenix",
    tierNumber: 8,
    iconPath: "/assets/skysociety/phoenix.avif",
    requirements: {
      badges: {
        count: 50,
        description: "50+ Badges",
      },
      discord: {
        required: true,
        description: "@MoonGoon or @Goonfluencer role",
      },
      metrics: {
        description: "Hub Metrics Required",
        conditions: ["8 referrals", "60 day daily spin streak", "100 trades", "Staked >100 Plume for over 30 days"],
        logicOperator: "AND",
      },
    },
    rewards: {
      plumePoints: 2000,
      other: ["Exclusive Phoenix NFT", "Access to Phoenix events"],
    },
  },
];

// Helper function to get current tier
export function getTierDataById(tierId: SkySocietyTierId): SkySocietyTierData {
  return skySocietyTiers.find((tier) => tier.id === tierId) || skySocietyTiers[0];
}

// Helper function to get next tier
export function getNextTierData(currentTierId: SkySocietyTierId): SkySocietyTierData | null {
  const currentIndex = skySocietyTiers.findIndex((tier) => tier.id === currentTierId);
  if (currentIndex === -1 || currentIndex === skySocietyTiers.length - 1) {
    return null; // No next tier if at max or tier not found
  }
  return skySocietyTiers[currentIndex + 1];
}
